import { produce } from 'immer';
import { LOADING_STATES } from 'src/utils/maps';
import { DAILY_BOOST_CONSTANTS } from '../constants/daily-boost';

const initialState = {
  // GET /users/me/daily-boost payload (server truth)
  state: {
    data: null,
    status: LOADING_STATES.LOADING,
  },
  // Wheel payload from the boost response — {items, spottedItem}. The
  // prize is server-decided and already settling; the wheel is theater.
  // petName = the BOOSTED pet (threaded from the vote flow).
  spin: {
    data: null,
    petName: null,
    visible: false,
  },
  // Milestone celebration, shown after the spin closes. petImage feeds
  // the tier-up ceremony's "your profile leveled up" photo moment.
  milestone: {
    data: null,
    petName: null,
    petImage: null,
    open: false,
  },
};

const dailyBoostReducer = (state = initialState, action) => produce(state, (draft) => {
  switch (action.type) {
    case DAILY_BOOST_CONSTANTS.setStateData:
      draft.state.data = action.payload;
      draft.state.status = LOADING_STATES.LOADED;
      break;

    case DAILY_BOOST_CONSTANTS.setStateStatus:
      draft.state.status = action.payload;
      break;

    case DAILY_BOOST_CONSTANTS.setStateError:
      // Nothing renders the message — status is the signal.
      draft.state.status = LOADING_STATES.ERROR;
      break;

    case DAILY_BOOST_CONSTANTS.applyBoostResult: {
      const result = action.payload;
      if (draft.state.data) {
        // Never move the counter backward: a second tab's losing (already
        // boosted) vote returns the pre-increment count, and its response
        // can arrive after the winning tab already advanced the pill.
        if (result.currentDays >= (draft.state.data.currentDays || 0)) {
          draft.state.data.currentDays = result.currentDays;
          draft.state.data.badgeTier = result.badgeTier;
        }
        draft.state.data.boostedToday = true;
        draft.state.data.canBoostNow = false;
        if (result.currentDays > (draft.state.data.longestDays || 0)) {
          draft.state.data.longestDays = result.currentDays;
        }
        // The known daily amount, for the card's inline "+N on the way"
        // confirmation (regular days have no wheel reveal anymore).
        if (!result.alreadyBoostedToday && result.dailyReward) {
          draft.state.data.lastDailyReward = result.dailyReward;
        }
        // The day's FULL haul (daily + wheel + gift) — the ladder's
        // post-overlay flight on wheel days carries this number.
        if (!result.alreadyBoostedToday) {
          const spinVotes = (result.spin
            && result.spin.spottedItem
            && result.spin.spottedItem.votes) || 0;
          const giftVotes = (result.milestoneUnlocked
            && result.milestoneUnlocked.rewards
            && result.milestoneUnlocked.rewards.votes) || 0;
          draft.state.data.lastBoostTotal = (Number(result.dailyReward) || 0)
            + spinVotes + giftVotes;
        }
      }
      if (!result.alreadyBoostedToday && result.spin) {
        draft.spin.data = result.spin;
        draft.spin.petName = result.petName || null;
        draft.spin.visible = true;
      }
      if (result.milestoneUnlocked) {
        // Carry the day's OTHER rewards along so the milestone modal can
        // present the full haul (wheel + gift + daily) as one total —
        // showing only the gift's votes right after a bigger wheel win
        // reads as a downgrade.
        draft.milestone.data = {
          ...result.milestoneUnlocked,
          spinVotes: (result.spin && result.spin.spottedItem && result.spin.spottedItem.votes) || 0,
          dailyReward: result.dailyReward || 0,
        };
        draft.milestone.petName = result.petName || null;
        draft.milestone.petImage = result.petImage || null;
        // Opens after the spin closes (closeSpin flips it) — or now, if
        // there is no spin to show.
        draft.milestone.open = !result.spin;
      } else if (!result.alreadyBoostedToday && result.currentDays === 1) {
        // First-ever boost of a chain: no milestone fires until day 2, so
        // day 1 gets the intro card explaining how the streak works.
        draft.milestone.data = { day: 1, intro: true };
        draft.milestone.petName = result.petName || null;
        draft.milestone.open = !result.spin;
      }
      break;
    }

    case DAILY_BOOST_CONSTANTS.closeSpin:
      draft.spin.visible = false;
      draft.spin.data = null;
      draft.spin.petName = null;
      if (draft.milestone.data) {
        draft.milestone.open = true;
      }
      break;

    case DAILY_BOOST_CONSTANTS.closeMilestone:
      draft.milestone.open = false;
      draft.milestone.data = null;
      draft.milestone.petName = null;
      draft.milestone.petImage = null;
      break;

    // The "?" button reuses the day-1 intro card as an on-demand
    // how-it-works explainer; with { celebrate } it renders the full
    // "{pet}'s streak has started!" variant (funnel voters get it on
    // their first pet-page visit — their day-1 boost happened where no
    // streak UI exists).
    case DAILY_BOOST_CONSTANTS.showIntro: {
      const celebrate = Boolean(action.payload && action.payload.celebrate);
      draft.milestone.data = { intro: true, help: !celebrate };
      draft.milestone.petName = (action.payload && action.payload.petName) || null;
      draft.milestone.open = true;
      break;
    }

    case DAILY_BOOST_CONSTANTS.reset:
      return initialState;

    default:
      break;
  }
});

export default dailyBoostReducer;
